import { useEffect, useState } from 'react';
import { ArrowDownToLine, Banknote } from 'lucide-react';
import ShopCreditPurchaseModal, { type PurchaseTab } from './ShopCreditPurchaseModal';
import { MONEY } from '@kioskgaming/ui';

export interface ShopCreditPurchaseTriggerProps {
  costRate?: number;
  creditBalance?: number | null;
  creditBalanceLoading?: boolean;
  onlineBalanceNet?: number | null;
  onlineBalanceLoading?: boolean;
  onBalancesRefresh?: () => void | Promise<void>;
  conversionCostRate?: number | null;
  className?: string;
  /** When true, opens the dialog on mount. */
  defaultOpen?: boolean;
  /** Tab the dialog opens on when `defaultOpen` fires (the debt banner's "Buy credits now" passes 'balance'). */
  defaultOpenTab?: PurchaseTab;
  /** Dashboard accent: blue matches Admin/SA/Agent; emerald kept for legacy callers. */
  tone?: 'emerald' | 'orange' | 'blue';
  /** Called after a purchase order is created (e.g. silent dashboard reload). */
  onOrdered?: () => void;
}

export default function ShopCreditPurchaseTrigger({
  costRate,
  creditBalance,
  creditBalanceLoading,
  onlineBalanceNet,
  onlineBalanceLoading,
  onBalancesRefresh,
  conversionCostRate,
  className = '',
  defaultOpen = false,
  defaultOpenTab = 'crypto',
  tone = 'emerald',
  onOrdered,
}: ShopCreditPurchaseTriggerProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [modalInitialTab, setModalInitialTab] = useState<PurchaseTab>('crypto');

  function openModal(tab: PurchaseTab) {
    setModalInitialTab(tab);
    setOpen(true);
  }

  useEffect(() => {
    if (defaultOpen) {
      setModalInitialTab(defaultOpenTab);
      setOpen(true);
    }
  }, [defaultOpen, defaultOpenTab]);

  const depositClass =
    tone === 'blue'
      ? 'border-blue-600 bg-blue-600 text-white hover:bg-blue-700'
      : tone === 'orange'
        ? 'border-orange-200 bg-white text-orange-900 hover:bg-orange-50'
        : 'border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700';
  const secondaryClass =
    tone === 'blue'
      ? 'border-blue-600/40 bg-white text-blue-800 hover:bg-blue-50'
      : 'border-stone-200 bg-white text-emerald-800 hover:bg-emerald-50';

  return (
    <>
      <div className={`flex flex-col items-stretch gap-2 ${className}`}>
        <button
          type="button"
          onClick={() => openModal('crypto')}
          className={`inline-flex w-full items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold shadow-sm transition ${depositClass}`}
        >
          <ArrowDownToLine className="h-4 w-4 shrink-0" aria-hidden />
          {MONEY.addUsd}
        </button>
        <button
          type="button"
          onClick={() => openModal('balance')}
          className={`inline-flex w-full items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold shadow-sm transition ${secondaryClass}`}
        >
          <Banknote className="h-4 w-4 shrink-0" aria-hidden />
          {MONEY.buyCredits}
        </button>
      </div>
      <ShopCreditPurchaseModal
        open={open}
        onClose={() => setOpen(false)}
        costRate={costRate}
        creditBalance={creditBalance}
        creditBalanceLoading={creditBalanceLoading}
        onlineBalanceNet={onlineBalanceNet}
        onlineBalanceLoading={onlineBalanceLoading}
        onBalancesRefresh={onBalancesRefresh}
        initialTab={modalInitialTab}
        conversionCostRate={conversionCostRate}
        onOrdered={onOrdered}
      />
    </>
  );
}
